import * as React from "react";
import { connect } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import sortBy from "lodash/sortBy";

import { parseApiDate } from "../../helpers/date";
import {
  updateWorkout,
  UpdateWorkout,
  fetchUser,
  FetchUser,
  deleteWorkout,
  DeleteWorkout
} from "../../resources/user/user.actions";
import Header from "../../layouts/main/header";
import EditButton from "../../components/save-button";
import { getUser } from "../../resources/user/user.selectors";
import { IStore } from "../../resources/types";
import { IUser, IWorkout } from "../../resources/user/types";
import Button from "../../components/button";
import Exercise from "./exercise";
import Note from "./note";

import styles from "./exercises.module.css";

interface IProps {
  user: IUser | undefined;
  fetchUser: FetchUser;
  updateWorkout: UpdateWorkout;
  deleteWorkout: DeleteWorkout;
}

function Exercises({ user, fetchUser, updateWorkout, deleteWorkout }: IProps) {
  const history = useHistory();
  const { id, workoutId } = useParams<{ id: string; workoutId: string }>();
  const [workout, setWorkout] = React.useState<IWorkout | undefined>(undefined);
  const [confirm, setConfirm] = React.useState(true);
  const [showSaved, setShowSaved] = React.useState(false);

  React.useEffect(() => {
    fetchUser(id);
  }, [fetchUser, id]);

  React.useEffect(() => {
    if (user && user.logbook) {
      const data = user.logbook.find(we => we.id === workoutId);
      setWorkout(data);
    }
  }, [user, workoutId]);
  if (!user || !workout) {
    return null;
  }

  const onDateChange = async (newDate: string) => {
    if (newDate === moment(workout.date).format("YYYY-MM-DD")) {
      return;
    }

    await updateWorkout(id, workoutId, {
      date: moment(newDate).format(),
      timestamp: moment.utc(newDate).unix()
    });

    await fetchUser(id);

    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 1500);
  };

  async function onSubmit(note: string) {
    await updateWorkout(id, workoutId, { note });
  }

  const onClickDelete = () => setConfirm(false);

  const onDelete = async () => {
    await deleteWorkout(id, workoutId);

    history.push(`/users/${id}`);
  };

  const onClickEdit = () =>
    history.push(`/users/${id}/workout/edit/${workoutId}/edit`);

  const TitleComponent = () => (
    <div className={styles.headerContainer}>
      {/* eslint-disable-next-line react/prop-types */}
      <div className={styles.headerTitle}>{`${user.firstName}'s workout`}</div>
      <div className={styles.dateContainer}>
        <div className={styles.subTitle}>
          {parseApiDate(workout.date).format("ddd, MMM D, YYYY")}
          {showSaved && <span className={styles.checkmark}>&#10003;</span>}
        </div>
        <input
          type="date"
          className={styles.dateInputOverlay}
          value={parseApiDate(workout.date).format("YYYY-MM-DD")}
          onChange={e => onDateChange(e.target.value)}
        />
      </div>
    </div>
  );

  const RightButton = () => <EditButton onClick={onClickEdit}>edit</EditButton>;

  return (
    <>
      <Header
        className={styles.header}
        TitleComponent={TitleComponent}
        RightButton={RightButton}
      />
      <div className={styles.exercises}>
        {sortBy(workout.exercises || [], "pos").map(exercise => (
          <Exercise key={exercise.id} exercise={exercise} />
        ))}
      </div>
      <Note onSubmit={onSubmit} workout={workout} />
      <Button onClick={confirm ? onClickDelete : onDelete}>
        {confirm ? "Delete Workout" : "Tap again to confirm"}
      </Button>
    </>
  );
}
export default connect(
  (state: IStore) => ({
    user: getUser(state)
  }),
  {
    fetchUser,
    updateWorkout,
    deleteWorkout
  }
)(Exercises);
